import classNames from 'classnames';
import _ from 'lodash';
import React, {
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import AppBanner from '../../banners/AppBanner';
import ModalContainer from '../../components/ModalContainer';
import ToastContainer from '../../components/ToastContainer';
import VersionMismatchModal from '../../error/VersionMismatchModal';
import OnboardingContainer from '../../onboarding/OnboardingContainer';
import { UIActions } from '../../ui/uiSlice';
import PageMeta from '../../utils/PageMeta';
import { RIGHT_PANEL_SHIFT_BREAKPOINT } from '../../utils/responsive/Sizes';
import { IsPublicViewProvider } from '../../utils/useIsPublicView';
import { useUserUiTheme } from '../../utils/useUiTheme';
import useAppSelector, { useAppDispatch } from '../redux/useAppSelector';
import { IsSidebarOpenProvider } from './IsSidebarOpenContext';
import RightPanelContainer from './RightPanelContainer';
import {
  useShouldShiftContentForSidebar,
  useSidebarShiftBreakpoint,
} from './SidebarShiftStateProvider';

const useRightPanelState = () => {
  const isRightPanelOpen = useAppSelector(
    (state) => state.ui.rightPanel.isOpen,
  );

  const [shouldShiftContentForRightPanel, setShouldShiftContentForRightPanel] =
    useState(() => window.innerWidth < RIGHT_PANEL_SHIFT_BREAKPOINT);

  useEffect(() => {
    const handleResize = () => {
      setShouldShiftContentForRightPanel(
        window.innerWidth < RIGHT_PANEL_SHIFT_BREAKPOINT,
      );
    };

    handleResize();

    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    isRightPanelOpen,
    shouldShiftContentForRightPanel,
  };
};

export interface PageLayoutElements {
  sidebar?: JSX.Element;
  topbar?: JSX.Element;
  helpMenu?: JSX.Element;
}

interface InnerPageLayoutProps {
  elements: PageLayoutElements;
  routeName: string;
  children: React.ReactNode;
}
export const InnerPageLayout = ({
  elements: { sidebar, topbar, helpMenu },
  routeName,
  children,
}: InnerPageLayoutProps) => {
  const theme = useUserUiTheme();

  useLayoutEffect(() => {
    if (theme) {
      document.body.setAttribute('data-theme', theme.toLowerCase());
    }

    return () => {
      document.body.removeAttribute('data-theme');
    };
  }, [theme]);

  const dispatch = useAppDispatch();

  const { isRightPanelOpen, shouldShiftContentForRightPanel } =
    useRightPanelState();

  const shouldShiftContentForSidebar = useShouldShiftContentForSidebar();

  const hasInitializedSidebarStateRef = useRef(false);
  const sidebarShiftBreakpoint = useSidebarShiftBreakpoint();

  useEffect(() => {
    if (sidebar && !hasInitializedSidebarStateRef.current) {
      dispatch(
        UIActions.toggleSidebar(window.innerWidth >= sidebarShiftBreakpoint),
      );
      hasInitializedSidebarStateRef.current = true;
    }
  }, [sidebar, dispatch, sidebarShiftBreakpoint]);

  const isSidebarOpenPreferred = useAppSelector(
    (state) => state.ui.isSidebarOpenPreferred,
  );
  const isSidebarOpen = isSidebarOpenPreferred && !!sidebar;

  const location = useLocation();

  useEffect(() => {
    if (sidebar && window.innerWidth < sidebarShiftBreakpoint) {
      dispatch(UIActions.toggleSidebar(false));
    }
  }, [sidebar, location, dispatch, sidebarShiftBreakpoint]);

  const classes = classNames('app-root', {
    'should-shift-content-for-sidebar': shouldShiftContentForSidebar,
    'should-shift-content-for-right-panel': shouldShiftContentForRightPanel,
    'has-right-panel': isRightPanelOpen,
    sidebar: isSidebarOpen,
  });

  return (
    <IsSidebarOpenProvider isSidebarOpen={isSidebarOpen}>
      <IsPublicViewProvider>
        <div id={`${routeName}-view`} className={classes}>
          <PageMeta title={`${_.startCase(routeName)} - Slab`} />
          <AppBanner />
          <ModalContainer />
          <VersionMismatchModal />
          <ToastContainer />
          {sidebar}
          <main id="main">
            {topbar}
            <div id="inner">
              <Suspense fallback={null}>{children}</Suspense>
              <RightPanelContainer
                shiftContent={shouldShiftContentForRightPanel}
              />
              {helpMenu}
            </div>
          </main>
          <div
            id="overlay"
            onClick={() => {
              if (isRightPanelOpen) {
                dispatch(UIActions.closeRightPanel());
              } else if (isSidebarOpen) {
                dispatch(UIActions.toggleSidebar(false));
              }
            }}
          />
          <OnboardingContainer />
        </div>
      </IsPublicViewProvider>
    </IsSidebarOpenProvider>
  );
};

interface PageLayoutProps {
  elements: PageLayoutElements;
  routeName: string;
}

const PageLayout = (props: PageLayoutProps) => (
  <InnerPageLayout {...props}>
    <Outlet />
  </InnerPageLayout>
);

export default PageLayout;
